import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { RouterModule } from '@angular/router';
import {
  CompletedReport,
  PersonalizedReport,
  ScheduledReport,
} from '../../../models/api/reports/all-reports-response';
import { ArchivedReports } from '../../../models/api/reports/archived-reports';
import { Action } from '../../../models/core/action';
import { CasePipe } from '../../../pipes/case.pipe';
import { ReportsService } from '../../../services/reports/reports.service';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { ValidatorsEx } from '../../../validators/validators-ex';
import { BaseComponent } from '../../base/base.component';
import { ActionButtonComponent } from '../../core/buttons/action-button.component';
import { ToggleSwitchComponent } from '../../core/controls/toggle-switch.component';
import { RenameDialogComponent } from '../../core/dialogs/rename-dialog.component';
import { LinkLabelComponent } from '../../core/labels/link-label.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { MenuComponent } from '../../core/panels/menu.component';
import { ColumnComponent } from '../../core/table/column.component';
import { TableComponent } from '../../core/table/table.component';
import { ShareDialogComponent } from '../core/share-dialog.component';
import { FormatPipe } from '../../../pipes/format.pipe';

@Component({
  selector: 'app-my-reports',
  imports: [
    TableComponent,
    ColumnComponent,
    MenuComponent,
    CommonModule,
    ActionButtonComponent,
    RouterModule,
    LinkLabelComponent,
    ShareDialogComponent,
    RenameDialogComponent,
    ToggleSwitchComponent,
    ActionRowComponent,
    CasePipe,
    FormatPipe,
  ],
  template: `
    <div>
      <div class="tw-pt-2">
        <app-action-row>
          <app-toggle-switch
            right1
            [control]="showArchived"
            label="View archived reports"
            [showHelpText]="false"
          />
        </app-action-row>

        <app-table
          [dataSource]="
            showArchived.value ? archivedReports : personalizedReports()
          "
          (actionClick)="actionMenu.toggle($event)"
          recordLabel="personalized report"
          dataKey="requestId"
          [ariaTableLabel]="'Personalized report'"
          scrollHeight="2000px"
          [stripedRows]="false"
          [loading]="loading()"
          (refresh)="refresh()"
          [showRefresh]="true"
          [showFilterButton]="false"
          [showFilter]="false"
          sortField="updatedDate"
          [sortOrder]="-1"
          emptyIcon="fa-file-lines fa-light"
          emptyMessage="Get started by creating a new report."
        >
          <app-column
            name="reportName"
            label="Name"
            filterType="select"
            [wrap]="true"
            minWidth="320px"
            maxWidth="420px"
          >
            <ng-template let-data>
              <div
                class="tw-flex tw-items-center tw-gap-2 tw-max-w-full tw-break-words tw-whitespace-normal"
              >
                <app-link-label
                  [href]="getReportDetailHref(data)"
                  (click)="viewReportDetails(data)"
                  style="cursor: pointer;"
                >
                  {{ data.reportName }}
                </app-link-label>
              </div>
            </ng-template>
          </app-column>

          <app-column
            name="outputName"
            label="Base report"
            filterType="select"
            [fullRow]="false"
            ><ng-template let-data
              ><div class="tw-max-w-full tw-break-words tw-whitespace-normal">
                {{ data.outputName }}
              </div>
            </ng-template>
          </app-column>
          <app-column name="filterName" label="Filter" filterType="select">
            <ng-template let-data
              ><div class="tw-max-w-full tw-break-words tw-whitespace-normal">
                {{ data.filterName }}
              </div>
            </ng-template>
          </app-column>
          <app-column
            name="contract"
            label="Client"
            filterType="select"
            filterLabelName="clientName"
            filterLabelCase="titlecase"
          >
            <ng-template let-data>
              <div class="tw-max-w-full tw-break-words tw-whitespace-normal">
                @if (data.contract && data.clientName) {
                  {{ data.contract }} -
                  {{ data.clientName | case: 'titlecase' }}
                }
              </div>
            </ng-template>
          </app-column>
          <app-column
            name="updatedDate"
            label="Updated"
            format="dt"
            filterType="date"
            ><ng-template let-data
              ><div class="tw-max-w-full tw-break-words tw-whitespace-normal">
                {{ data.updatedDate | format: 'dt' }}
              </div>
            </ng-template>
          </app-column>
          <app-column
            name="actions"
            label="Actions"
            classes=""
            [frozen]="true"
            [sort]="false"
          >
            <ng-template let-data>
              <div class="tw-max-w-full tw-break-words tw-whitespace-normal">
                <app-action-button
                  [ariaLabel]="'More actions for ' + data.reportName"
                  [menu]="rowMenu"
                  [row]="data"
                  (click)="rowMenu.toggle($event, data)"
                />
              </div>
            </ng-template>
          </app-column>
        </app-table>
        <app-menu
          [items]="currentRowActions"
          #rowMenu
          (click)="onAction($event, rowMenu.data)"
        />
        <app-menu
          [items]="actions"
          #actionMenu
          (click)="onAction($event, null)"
        />
      </div>
      @if (currentDataSource.length > 0) {
        <p class="tw-text-sm tw-italic tw-text-gray-600 tw-mt-4">
          This tab shows your personalized reports; the ones you've saved with
          your preferred filters, settings, and other custom criteria.
        </p>
      }
    </div>
    <app-rename-dialog
      label="Rename report"
      #renameDialog
      [extraValidators]="reportNameValidators"
      (rename)="onRenameConfirm($event)"
    />
    <app-rename-dialog
      label="Copy report"
      buttonLabel="Copy"
      [allowSameValue]="true"
      #copyDialog
      [extraValidators]="reportNameValidators"
      (rename)="onCopyConfirm($event)"
    />
    <app-rename-dialog
      label="Create personalized report"
      buttonLabel="Create"
      [allowSameValue]="true"
      #createPersonalizedDialog
      [extraValidators]="reportNameValidators"
      (rename)="onCreatePersonalizedConfirm($event)"
    />
    <app-share-dialog #shareDialog />
  `,
  styles: ``,
})
export class MyReportsComponent extends BaseComponent implements OnInit {
  @ViewChild('renameDialog')
  renameDialog: RenameDialogComponent;

  @ViewChild('copyDialog')
  copyDialog: RenameDialogComponent;

  @ViewChild('createPersonalizedDialog')
  createPersonalizedDialog: RenameDialogComponent;

  @ViewChild('shareDialog')
  shareDialog: ShareDialogComponent;

  private readonly reportsService = inject(ReportsService);
  private readonly snackbar = inject(Snackbar);

  reportNameValidators = [
    ValidatorsEx.alphaNumericWith(
      '/-_ ',
      'Slashes (/), dashes (-), and underscores (_) are the only special characters allowed in report name.',
    ),
  ];

  showArchived = new FormControl<boolean>(false);

  actions: Action[] = [{ label: 'Delete selected' }, { label: 'Delete all' }];

  readonly personalizedReports = this.reportsService.personalizedReports;
  readonly loading = this.reportsService.personalizedLoading;

  personalizedRowActions: Action[] = [
    { label: 'View details', key: 'view' },
    { label: 'New schedule', key: 'schedule' },
    { label: 'Edit report', key: 'edit' },
    { label: 'Copy', key: 'copy' },
    { label: 'Rename', key: 'rename' },
    { label: 'Share', key: 'share' },
    { label: 'Delete', key: 'delete' },
  ];

  archivedRowActions: Action[] = [
    { label: 'View details', key: 'view' },
    { label: 'Create personalized', key: 'create-personalized' },
    { label: 'Share', key: 'share' },
    { label: 'Delete', key: 'delete' },
  ];

  get currentRowActions(): Action[] {
    return this.showArchived.value
      ? this.archivedRowActions
      : this.personalizedRowActions;
  }

  archivedReports: ArchivedReports[] = [];

  private activeReport: PersonalizedReport | ArchivedReports | null = null;

  get currentDataSource() {
    return this.showArchived.value
      ? this.archivedReports
      : this.personalizedReports();
  }

  async ngOnInit() {
    await this.reportsService.getReports();

    this.dispose.subs = this.showArchived.valueChanges.subscribe(
      (isArchived) => {
        if (isArchived) {
          this.getArchivedReports();
        }
      },
    );
  }

  async onAction(
    action: Action,
    report: PersonalizedReport | ArchivedReports | null,
  ) {
    if (!report) {
      return;
    }

    this.activeReport = report;

    if (action.key === 'delete') {
      const isArchived = this.showArchived.value && 'type' in report;
      const archiveWarning = isArchived
        ? 'Deleting reports from this grid will also delete them from the Scheduled/Completed Reports grids. '
        : '';
      this.confirm.show({
        label: 'Delete report',
        message: `${archiveWarning}Are you sure you want to delete "${report.reportName}"?`,
        width: '35rem',
        data: report,
        confirm: (data) => this.deleteReport(data),
      });
    } else if (action.key === 'share') {
      const requestId = this.getRequestId(report);
      this.shareDialog.show(
        'report',
        requestId,
        parseInt(report.contract, 10),
        report.reportName,
        report.reportMasterId,
      );
    } else if (action.key === 'edit') {
      const personalizedReport = report as PersonalizedReport;
      await this.reportsService.loadPersonalizedRequestForEditing(
        personalizedReport.requestId,
        'JURIS',
        personalizedReport.model,
        personalizedReport.contract,
      );
      this.reportsService.setMode('edit-personalized');
      this.router.navigate(['/reports/edit-report'], {
        queryParams: { step: 'configure' },
      });
    } else if (action.key === 'view') {
      this.viewReportDetails(report);
    } else if (action.key === 'schedule') {
      const personalizedReport = report as PersonalizedReport;
      await this.reportsService.loadPersonalizedRequestForEditing(
        personalizedReport.requestId,
        'JURIS',
        personalizedReport.model,
        personalizedReport.contract,
      );
      this.reportsService.setMode('schedule-personalized');
      this.router.navigate(['/reports/edit-report'], {
        queryParams: { step: 'delivery' },
      });
    } else if (action.key === 'copy') {
      this.copyDialog.show(report.reportName + ' - Copy');
    } else if (action.key === 'rename') {
      this.renameDialog.show(report.reportName);
    } else if (action.key === 'create-personalized') {
      this.createPersonalizedDialog.show(report.reportName);
    }
  }

  async refresh() {
    await this.reportsService.getReports(true);
  }

  async deleteReport(report: PersonalizedReport | ArchivedReports) {
    if ('type' in report) {
      const archived = report as ArchivedReports;
      if (archived.type === 'completed') {
        const completed = archived as CompletedReport;
        await this.reportsService.deleteCompletedReport(
          completed.completedRequestId,
          completed.completedRequestOutputId,
        );
        this.archivedReports = this.archivedReports.filter((r) => r !== report);
      } else {
        const scheduled = archived as ScheduledReport;
        const response = await this.reportsService.deleteScheduledReport(
          scheduled.requestId,
        );
        if (response.success) {
          this.archivedReports = this.archivedReports.filter(
            (r) => r !== report,
          );
        } else {
          this.snackbar.show(
            response.message,
            'Error',
            'fa-light fa-circle-exclamation',
            5000,
          );
        }
      }
    } else {
      await this.reportsService.deletePersonalizedReport(report.requestId);
    }
  }

  async doCopyReport(newName: string, report: PersonalizedReport) {
    this.copyDialog.setLoading(true);
    await this.reportsService.copyPersonalizedReport(report.requestId, newName);
    this.snackbar.show('Report copied', 'Success', 'fa-light fa-copy', 3000);
    this.copyDialog.close();
  }

  async doCreatePersonalized(newName: string, report: ArchivedReports) {
    this.createPersonalizedDialog.setLoading(true);
    const requestId = this.getRequestId(report);
    await this.reportsService.createPersonalizedFromArchived(
      requestId,
      newName,
    );
    this.snackbar.show(
      'Personalized report created',
      'Success',
      'fa-light fa-check',
      3000,
    );
    this.createPersonalizedDialog.close();
  }

  async renameReport(newName: string, report: PersonalizedReport) {
    this.renameDialog.setLoading(true);

    await this.reportsService.renamePersonalizedReport(
      report.requestId,
      newName,
    );

    this.snackbar.show(
      'Report renamed successfully',
      'Success',
      'fa-light fa-check',
      3000,
    );

    this.renameDialog.close();
  }

  protected onRenameConfirm(newName: string): void {
    if (this.activeReport && !('type' in this.activeReport)) {
      void this.renameReport(newName, this.activeReport);
    }
  }

  protected onCopyConfirm(newName: string): void {
    if (this.activeReport && !('type' in this.activeReport)) {
      void this.doCopyReport(newName, this.activeReport);
    }
  }

  protected onCreatePersonalizedConfirm(newName: string): void {
    if (this.activeReport && 'type' in this.activeReport) {
      void this.doCreatePersonalized(
        newName,
        this.activeReport as ArchivedReports,
      );
    }
  }

  getReportDetailHref(report: PersonalizedReport | ArchivedReports): string {
    if (this.showArchived.value && 'type' in report) {
      const archived = report as ArchivedReports;
      return this.reportsService.getDetailUrl(
        archived,
        archived.type === 'completed' ? 'completed' : 'scheduled',
      );
    }
    return this.reportsService.getDetailUrl(
      report as PersonalizedReport,
      'personalized',
    );
  }

  viewReportDetails(report: PersonalizedReport | ArchivedReports) {
    if (this.showArchived.value && 'type' in report) {
      const archived = report as ArchivedReports;
      if (archived.type === 'completed') {
        this.reportsService.navigateToDetail(archived, 'completed');
      } else {
        this.reportsService.navigateToDetail(archived, 'scheduled');
      }
    } else {
      this.reportsService.navigateToDetail(
        report as PersonalizedReport,
        'personalized',
      );
    }
  }

  private getRequestId(report: PersonalizedReport | ArchivedReports): string {
    if ('type' in report) {
      const archived = report as ArchivedReports;
      if (archived.type === 'completed') {
        return (archived as CompletedReport).templateRequestId ?? '';
      }
      return (archived as ScheduledReport).requestId ?? '';
    }
    return (report as PersonalizedReport).requestId ?? '';
  }

  async getArchivedReports() {
    await this.reportsService.getReports();

    const completedReports = this.reportsService.completedReports();
    const scheduledReports = this.reportsService.scheduledReports();

    this.archivedReports = [
      ...completedReports.map((r) => ({
        ...r,
        type: 'completed' as const,
        requestId: r.templateRequestId,
        updatedDate: r.dateCompleted,
      })),
      ...scheduledReports.map((r) => ({ ...r, type: 'scheduled' as const })),
    ];
  }
}
